import { Info } from "lucide-react";
import { type JSX, type ReactNode, useId, useState } from "react";
import { cx } from "../cx";
import { IconButton } from "./IconButton";

export interface PageHeaderProps {
  /** Surface title, rendered as the route's h2. */
  title: string;
  /**
   * Backend-delivered collection count. Render only when the number is a
   * real collection size that serves the route's intent — never a sum of
   * unrelated tab contents.
   */
  count?: number | undefined;
  /** Primary header actions (buttons, pickers). */
  actions?: ReactNode | undefined;
  /**
   * Framing prose for the surface. Collapsed by default behind the
   * "About this page" affordance so it never occupies primary space.
   */
  about?: ReactNode | undefined;
  className?: string | undefined;
  /** Optional extra header content (status pills, scope notes). */
  children?: ReactNode | undefined;
}

/**
 * Quiet, intent-first surface header: title, optional real count,
 * actions, and framing prose behind a deliberate info affordance.
 * Replaces hand-rolled `surface-head` + framing paragraphs.
 */
export function PageHeader({
  title,
  count,
  actions,
  about,
  className,
  children,
}: PageHeaderProps): JSX.Element {
  const aboutId = useId();
  const [aboutOpen, setAboutOpen] = useState(false);

  return (
    <header className={cx("tk-page-header", className)}>
      <div className="tk-page-header__row">
        <h2 className="tk-page-header__title">{title}</h2>
        {count === undefined ? null : <span className="tk-page-header__count">{count}</span>}
        {about === undefined ? null : (
          <IconButton
            label={`About ${title}`}
            size="sm"
            aria-expanded={aboutOpen}
            aria-controls={aboutId}
            className={cx("tk-page-header__about-toggle", aboutOpen && "is-open")}
            onClick={() => setAboutOpen((current) => !current)}
          >
            <Info size={14} aria-hidden="true" />
          </IconButton>
        )}
        {children}
        {actions === undefined ? null : <div className="tk-page-header__actions">{actions}</div>}
      </div>
      {about === undefined ? null : (
        <div id={aboutId} hidden={!aboutOpen}>
          {aboutOpen ? (
            <p className="tk-page-header__about" role="note">
              {about}
            </p>
          ) : null}
        </div>
      )}
    </header>
  );
}
